import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Grid,
  Card, CardContent, LinearProgress, Tooltip, Tab, Tabs, Switch, FormControlLabel,
  Divider, Alert, CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Autocomplete
} from '@mui/material';
import {
  Add, Edit, Send, Schedule, ContentCopy, AutoAwesome, Email, Sms, WhatsApp,
  Notifications, ExpandMore, Analytics, Campaign, PlayArrow, Lightbulb, Psychology,
  Link as LinkIcon
} from '@mui/icons-material';
import KooppaiIcon from './KooppaiIcon';
import CTALinkBuilder from './CTALinkBuilder';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { HEADERS } from '../../../API/UrlAndPaths';

const CampaignManagement = () => {
  const currencySymbol = useSelector((state) => state.mainDashBoardSlice?.currencyDetails?.symbol) || '₹';
  const [campaigns, setCampaigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiVariations, setAiVariations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [ctaBuilderOpen, setCtaBuilderOpen] = useState(false);
  const [ctaBuilderCampaign, setCtaBuilderCampaign] = useState(null);

  const [formData, setFormData] = useState({
    name: '', type: 'KOOPPAI', goal: 'PROMOTION', subject: '',
    content: '', previewText: '', ctaText: '', ctaUrl: '', offerDetails: '',
    offerCode: '', scheduledDate: '', isAbTest: false, isAiGenerated: false
  });

  useEffect(() => {
    fetchCampaigns();
    fetchAnalytics();
    fetchCustomers();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/campaigns', HEADERS);
      if (res.data.success) setCampaigns(res.data.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get('/api/campaigns/analytics/dashboard', HEADERS);
      if (res.data.success) setAnalytics(res.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.post('account/viewCustomerForDropdown', {}, HEADERS);
      if (res.status === 200) {
        setCustomers(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const generateAiContent = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post('/api/campaigns/ai/generate-content', {
        campaignType: formData.type,
        businessName: localStorage.getItem('companyName') || 'Our Business',
        campaignGoal: formData.goal,
        offerDetails: formData.offerDetails,
        targetAudience: selectedCustomers.length > 0 ? `${selectedCustomers.length} selected customers` : 'All customers',
        tone: 'friendly'
      }, HEADERS);

      console.log('AI Response:', res.data);
      if (res.data.success) {
        let content = res.data.data;
        console.log('AI Content:', content);
        
        // Handle multi-channel response format: {sms: {...}, email: {...}, whatsapp: {...}}
        // Extract content based on campaign type
        const channelMap = {
          'KOOPPAI': 'whatsapp',
          'WHATSAPP': 'whatsapp', 
          'SMS': 'sms',
          'EMAIL': 'email'
        };
        const channelKey = channelMap[formData.type] || 'whatsapp';
        
        if (content[channelKey]) {
          content = content[channelKey];
          console.log('Extracted channel content:', channelKey, content);
        }
        
        setFormData(prev => ({
          ...prev,
          subject: content.subject || prev.subject,
          content: content.body || content.message || '',
          ctaText: content.cta || prev.ctaText,
          previewText: content.previewText || prev.previewText,
          isAiGenerated: true
        }));
        Swal.fire('Success', 'AI content generated!', 'success');
      } else {
        console.log('AI Response not successful:', res.data);
        Swal.fire('Error', res.data.message || 'Failed to generate content', 'error');
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to generate AI content. Please configure AI API key in settings.', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const generateAiVariations = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post('/api/campaigns/ai/generate-variations', {
        campaignType: formData.type,
        businessName: localStorage.getItem('companyName') || 'Our Business',
        campaignGoal: formData.goal,
        offerDetails: formData.offerDetails,
        count: 3
      }, HEADERS);

      if (res.data.success) {
        setAiVariations(res.data.data);
        Swal.fire('Success', `Generated ${res.data.data.length} variations!`, 'success');
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to generate variations', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleOpenDialog = (campaign = null) => {
    if (campaign) {
      setSelectedCampaign(campaign);
      setFormData({
        name: campaign.name, type: campaign.type, goal: campaign.goal || 'PROMOTION',
        subject: campaign.subject || '',
        content: campaign.content, previewText: campaign.previewText || '',
        ctaText: campaign.ctaText || '', ctaUrl: campaign.ctaUrl || '',
        offerDetails: campaign.offerDetails || '', offerCode: campaign.offerCode || '',
        scheduledDate: campaign.scheduledDate?.split('T')[0] || '',
        isAbTest: campaign.isAbTest || false, isAiGenerated: campaign.isAiGenerated || false
      });
      // Load selected customers from campaign
      setSelectedCustomers(campaign.targetCustomers || []);
    } else {
      setSelectedCampaign(null);
      setFormData({
        name: '', type: 'KOOPPAI', goal: 'PROMOTION', subject: '',
        content: '', previewText: '', ctaText: '', ctaUrl: '', offerDetails: '',
        offerCode: '', scheduledDate: '', isAbTest: false, isAiGenerated: false
      });
      setSelectedCustomers([]);
    }
    setAiVariations([]);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...formData };
      if (aiVariations.length > 0) {
        payload.aiVariations = aiVariations.map((v, i) => ({
          variation: String.fromCharCode(65 + i), approach: v.approach,
          subject: v.subject, content: v.message, cta: v.cta
        }));
      }
      
      // Add selected customers to payload
      if (selectedCustomers.length > 0) {
        payload.targetCustomers = selectedCustomers.map(c => c._id);
        payload.targetCustomerDetails = selectedCustomers.map(c => ({
          customerId: c._id,
          name: c.customerName || c.name,
          phone: c.mobileNo || c.phone,
          email: c.email
        }));
        console.log('📋 Saving campaign with', selectedCustomers.length, 'selected customers');
        console.log('📋 Target customers:', payload.targetCustomers);
        console.log('📋 Target customer details:', payload.targetCustomerDetails);
      } else {
        console.log('📋 No customers selected - will send to all');
      }

      if (selectedCampaign) {
        await axios.patch(`/api/campaigns/${selectedCampaign._id}`, payload, HEADERS);
      } else {
        await axios.post('/api/campaigns', payload, HEADERS);
      }
      fetchCampaigns();
      setDialogOpen(false);
      Swal.fire('Success', 'Campaign saved!', 'success');
    } catch (error) {
      console.error('Save campaign error:', error);
      Swal.fire('Error', 'Failed to save campaign', 'error');
    }
  };

  const handleSendCampaign = async (id, forceResend = false) => {
    const result = await Swal.fire({
      title: 'Send Campaign?', text: 'This will send to all targeted customers.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Send Now'
    });
    if (result.isConfirmed) {
      // Show loading
      Swal.fire({
        title: '📧 Sending Campaign...',
        html: 'Please wait while emails are being sent.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });
      
      try {
        const url = forceResend ? `/api/campaigns/${id}/send?resend=true` : `/api/campaigns/${id}/send`;
        const res = await axios.post(url, {}, HEADERS);
        fetchCampaigns();
        Swal.fire({
          icon: 'success',
          title: 'Campaign Sent!',
          html: res.data.message || `Successfully sent to ${res.data.stats?.sent || 0} recipients.`,
          timer: 3000
        });
      } catch (error) {
        // Check if campaign was already sent and can be resent
        if (error.response?.data?.canResend) {
          const resendResult = await Swal.fire({
            title: 'Campaign Already Sent',
            text: 'Do you want to send it again?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Resend'
          });
          if (resendResult.isConfirmed) {
            Swal.fire({
              title: '📧 Resending Campaign...',
              html: 'Please wait while emails are being sent.',
              allowOutsideClick: false,
              allowEscapeKey: false,
              didOpen: () => Swal.showLoading()
            });
            try {
              const res = await axios.post(`/api/campaigns/${id}/send?resend=true`, {}, HEADERS);
              fetchCampaigns();
              Swal.fire({
                icon: 'success',
                title: 'Campaign Sent!',
                html: res.data.message || `Successfully sent to ${res.data.stats?.sent || 0} recipients.`
              });
            } catch (resendError) {
              Swal.fire('Error', resendError.response?.data?.message || 'Failed to resend', 'error');
            }
          }
        } else {
          Swal.fire('Error', error.response?.data?.message || 'Failed to send', 'error');
        }
      }
    }
  };

  const handleScheduleCampaign = async (id) => {
    const { value: date } = await Swal.fire({
      title: 'Schedule Campaign', input: 'datetime-local', showCancelButton: true
    });
    if (date) {
      try {
        await axios.post(`/api/campaigns/${id}/schedule`, { scheduledDate: date }, HEADERS);
        fetchCampaigns();
        Swal.fire('Success', 'Scheduled!', 'success');
      } catch (error) {
        Swal.fire('Error', 'Failed to schedule', 'error');
      }
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`/api/campaigns/${id}/duplicate`, {}, HEADERS);
      fetchCampaigns();
      Swal.fire('Success', 'Duplicated!', 'success');
    } catch (error) {
      Swal.fire('Error', 'Failed', 'error');
    }
  };

  const handleAnalyze = async (id) => {
    setAiLoading(true);
    try {
      const res = await axios.post(`/api/campaigns/${id}/ai/analyze-results`, {}, HEADERS);
      if (res.data.success) {
        const a = res.data.data;
        // Format recommendations
        const recsHtml = a.recommendations?.map(r => 
          typeof r === 'string' ? `<li>${r}</li>` : 
          `<li><b>${r.priority || ''}</b>: ${r.action || r} <i>(${r.expectedImpact || ''})</i></li>`
        ).join('') || '-';
        
        Swal.fire({
          title: `📊 Analysis - Grade: ${a.score || a.performanceScore || 'N/A'}`,
          html: `
            <div style="text-align: left;">
              <p><b>Summary:</b> ${a.summary || '-'}</p>
              <p><b>✅ Strengths:</b> ${a.strengths?.join(', ') || '-'}</p>
              <p><b>⚠️ Weaknesses:</b> ${a.weaknesses?.join(', ') || '-'}</p>
              <p><b>📋 Recommendations:</b></p>
              <ul style="text-align: left; font-size: 14px;">${recsHtml}</ul>
              ${a.nextCampaignSuggestion ? `<p><b>💡 Next Campaign:</b> ${a.nextCampaignSuggestion}</p>` : ''}
            </div>
          `,
          width: 700
        });
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Analysis failed', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    // KOOPPAI is primary channel with custom logo
    const icons = { 
      KOOPPAI: <KooppaiIcon sx={{ color: '#1976d2' }} />, 
      EMAIL: <Email />, 
      SMS: <Sms />, 
      WHATSAPP: <WhatsApp sx={{ color: '#25D366' }} />, 
      NOTIFICATION: <Notifications /> 
    };
    return icons[type] || <Campaign />;
  };

  const getStatusColor = (status) => {
    const colors = { DRAFT: 'default', SCHEDULED: 'info', SENDING: 'warning', SENT: 'success', CANCELLED: 'error' };
    return colors[status] || 'default';
  };

  const filtered = tabValue === 0 ? campaigns :
    tabValue === 1 ? campaigns.filter(c => c.status === 'DRAFT') :
    tabValue === 2 ? campaigns.filter(c => c.status === 'SCHEDULED') :
    campaigns.filter(c => c.status === 'SENT');

  return (
    <Box sx={{ p: 3 }}>
      {/* Analytics */}
      {analytics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Total Campaigns</Typography>
              <Typography variant="h4">{analytics.summary?.totalCampaigns || 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Messages Sent</Typography>
              <Typography variant="h4">{analytics.summary?.totalSent?.toLocaleString() || 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Avg Open Rate</Typography>
              <Typography variant="h4" color="primary">{analytics.summary?.avgOpenRate || 0}%</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Revenue</Typography>
              <Typography variant="h4" color="success.main">
                {currencySymbol}
                {analytics.summary?.totalRevenue?.toLocaleString() || 0}
              </Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>🚀 AI-Powered Campaigns</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Create Campaign
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`All (${campaigns.length})`} />
          <Tab label={`Draft (${campaigns.filter(c => c.status === 'DRAFT').length})`} />
          <Tab label={`Scheduled (${campaigns.filter(c => c.status === 'SCHEDULED').length})`} />
          <Tab label={`Sent (${campaigns.filter(c => c.status === 'SENT').length})`} />
        </Tabs>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Campaign</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Goal</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Stats</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}><LinearProgress /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No campaigns</TableCell></TableRow>
            ) : filtered.map(c => (
              <TableRow key={c._id} hover>
                <TableCell>
                  <Typography fontWeight={500}>{c.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {c.isAiGenerated && '🤖 AI Generated'}
                  </Typography>
                </TableCell>
                <TableCell><Chip icon={getTypeIcon(c.type)} label={c.type} size="small" /></TableCell>
                <TableCell><Chip label={c.goal} size="small" color="primary" variant="outlined" /></TableCell>
                <TableCell><Chip label={c.status} size="small" color={getStatusColor(c.status)} /></TableCell>
                <TableCell>
                  {c.status === 'SENT' ? (
                    <Box>
                      <Typography variant="caption">Sent: {c.stats?.sent || 0}</Typography><br/>
                      <Typography variant="caption">Clicked: {c.stats?.clicked || 0}</Typography>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenDialog(c)}><Edit /></IconButton></Tooltip>
                  {/* Show Send button for DRAFT and SCHEDULED campaigns */}
                  {(c.status === 'DRAFT' || c.status === 'SCHEDULED') && (
                    <Tooltip title="Send Now"><IconButton size="small" color="success" onClick={() => handleSendCampaign(c._id)}><Send /></IconButton></Tooltip>
                  )}
                  {/* Show Schedule button only for DRAFT */}
                  {c.status === 'DRAFT' && (
                    <Tooltip title="Schedule"><IconButton size="small" color="info" onClick={() => handleScheduleCampaign(c._id)}><Schedule /></IconButton></Tooltip>
                  )}
                  {/* Show Analytics for SENT campaigns */}
                  {c.status === 'SENT' && (
                    <Tooltip title="AI Analysis"><IconButton size="small" color="secondary" onClick={() => handleAnalyze(c._id)}><Analytics /></IconButton></Tooltip>
                  )}
                  <Tooltip title="Duplicate"><IconButton size="small" onClick={() => handleDuplicate(c._id)}><ContentCopy /></IconButton></Tooltip>
                  <Tooltip title="Create CTA Link"><IconButton size="small" color="primary" onClick={() => { setCtaBuilderCampaign(c); setCtaBuilderOpen(true); }}><LinkIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedCampaign ? 'Edit Campaign' : 'Create AI Campaign'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Campaign Name *" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth><InputLabel>Channel</InputLabel>
                <Select value={formData.type} label="Channel" onChange={e => setFormData({...formData, type: e.target.value})}>
                  <MenuItem value="KOOPPAI">💬 Kooppai (Recommended)</MenuItem>
                  <MenuItem value="EMAIL">📧 Email</MenuItem>
                  <MenuItem value="SMS">📱 SMS</MenuItem>
                  <MenuItem value="WHATSAPP">💬 WhatsApp</MenuItem>
                  <MenuItem value="NOTIFICATION">🔔 Push Notification</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth><InputLabel>Goal</InputLabel>
                <Select value={formData.goal} label="Goal" onChange={e => setFormData({...formData, goal: e.target.value})}>
                  <MenuItem value="PROMOTION">🎁 Promotion</MenuItem>
                  <MenuItem value="RETENTION">🤝 Retention</MenuItem>
                  <MenuItem value="REACTIVATION">🔄 Reactivation</MenuItem>
                  <MenuItem value="LOYALTY">⭐ Loyalty</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Offer Details" value={formData.offerDetails} onChange={e => setFormData({...formData, offerDetails: e.target.value})} placeholder="e.g., 20% off all orders" /></Grid>
            
            <Grid item xs={12}><Divider><Chip label="Content" /></Divider></Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button variant="contained" color="secondary" startIcon={aiLoading ? <CircularProgress size={20} /> : <AutoAwesome />} onClick={generateAiContent} disabled={aiLoading}>
                  Generate with AI
                </Button>
                <Button variant="outlined" startIcon={<Psychology />} onClick={generateAiVariations} disabled={aiLoading}>
                  A/B Variations
                </Button>
              </Box>
            </Grid>

            {formData.type === 'EMAIL' && (
              <Grid item xs={12}><TextField fullWidth label="Subject" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} /></Grid>
            )}
            <Grid item xs={12}><TextField fullWidth multiline rows={5} label="Message Content *" value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} helperText="Use {{customer_name}} for personalization" /></Grid>
            <Grid item xs={6}><TextField fullWidth label="CTA Text" value={formData.ctaText} onChange={e => setFormData({...formData, ctaText: e.target.value})} placeholder="Order Now" /></Grid>
            <Grid item xs={6}><TextField fullWidth label="CTA Link" value={formData.ctaUrl} onChange={e => setFormData({...formData, ctaUrl: e.target.value})} /></Grid>

            {aiVariations.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>{aiVariations.length} A/B variations generated</Alert>
                {aiVariations.map((v, i) => (
                  <Accordion key={i}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>Variation {String.fromCharCode(65 + i)}: {v.approach}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2"><b>Subject:</b> {v.subject}</Typography>
                      <Typography variant="body2"><b>Message:</b> {v.message}</Typography>
                      <Button size="small" sx={{ mt: 1 }} onClick={() => setFormData({...formData, subject: v.subject, content: v.message, ctaText: v.cta})}>
                        Use This
                      </Button>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            )}

            <Grid item xs={12}><Divider><Chip label="Targeting" /></Divider></Grid>
            
            <Grid item xs={12}><TextField fullWidth type="datetime-local" label="Schedule" value={formData.scheduledDate} onChange={e => setFormData({...formData, scheduledDate: e.target.value})} InputLabelProps={{ shrink: true }} /></Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={customers}
                getOptionLabel={(option) => option.customerName || option.name || ''}
                value={selectedCustomers}
                onChange={(e, newValue, reason) => {
                  // Filter out null values and check if "Select All" was clicked
                  const validValues = newValue.filter(v => v != null);
                  const selectAllOption = validValues.find(v => v?._id === 'SELECT_ALL');
                  if (selectAllOption) {
                    // If already all selected, deselect all; otherwise select all
                    if (selectedCustomers.length === customers.length) {
                      setSelectedCustomers([]);
                    } else {
                      setSelectedCustomers(customers);
                    }
                  } else {
                    setSelectedCustomers(validValues);
                  }
                }}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Customers" 
                    placeholder={selectedCustomers.length === 0 ? "Search customers..." : ""} 
                    helperText={selectedCustomers.length > 0 ? `${selectedCustomers.length} customer(s) selected` : "Leave empty to target all customers in segment"}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  // Destructure key from props to pass directly (React requirement)
                  const { key, ...otherProps } = props;
                  // Special "Select All" option
                  if (option._id === 'SELECT_ALL') {
                    const allSelected = selectedCustomers.length === customers.length;
                    return (
                      <Box 
                        key="select-all"
                        component="li" 
                        {...otherProps} 
                        sx={{ 
                          borderBottom: '1px solid #e0e0e0', 
                          bgcolor: '#f5f5f5',
                          fontWeight: 'bold',
                          '&:hover': { bgcolor: '#e3f2fd' }
                        }}
                      >
                        <input type="checkbox" checked={allSelected} readOnly style={{ marginRight: 8 }} />
                        <Typography variant="body2" fontWeight="bold">
                          {allSelected ? '✓ Deselect All' : '☐ Select All Customers'} ({customers.length})
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box key={option._id} component="li" {...otherProps} sx={{ display: 'flex', alignItems: 'center' }}>
                      <input type="checkbox" checked={selected} readOnly style={{ marginRight: 8 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="body2">{option.customerName || option.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.mobileNo || option.phone} {option.email ? `• ${option.email}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                filterOptions={(options, params) => {
                  // Add "Select All" option at the top
                  const filtered = options.filter(option => {
                    const label = (option.customerName || option.name || '').toLowerCase();
                    const phone = (option.mobileNo || option.phone || '').toLowerCase();
                    const searchTerm = params.inputValue.toLowerCase();
                    return label.includes(searchTerm) || phone.includes(searchTerm);
                  });
                  // Only show "Select All" when not searching
                  if (params.inputValue === '' && customers.length > 0) {
                    return [{ _id: 'SELECT_ALL', customerName: 'Select All' }, ...filtered];
                  }
                  return filtered;
                }}
                renderTags={(value, getTagProps) => {
                  // Filter out null values
                  const validValues = value.filter(v => v != null);
                  // Show summary chip if many selected
                  if (validValues.length > 3) {
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {validValues.slice(0, 2).map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option?._id || index}
                            label={option?.customerName || option?.name || 'Unknown'}
                            size="small"
                          />
                        ))}
                        <Chip
                          label={`+${validValues.length - 2} more`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    );
                  }
                  return validValues.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?._id || index}
                      label={option?.customerName || option?.name || 'Unknown'}
                      size="small"
                    />
                  ));
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={formData.isAbTest} onChange={e => setFormData({...formData, isAbTest: e.target.checked})} />} label="Enable A/B Testing" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save Campaign</Button>
        </DialogActions>
      </Dialog>

      {/* CTA Link Builder Dialog */}
      {ctaBuilderOpen && ctaBuilderCampaign && (
        <CTALinkBuilder
          campaignId={ctaBuilderCampaign._id}
          campaignName={ctaBuilderCampaign.name}
          onClose={() => { setCtaBuilderOpen(false); setCtaBuilderCampaign(null); }}
          onCreated={async (link) => {
            // Update campaign with the generated CTA URL
            try {
              await axios.patch(`/api/campaigns/${ctaBuilderCampaign._id}`, {
                ctaUrl: link.fullUrl,
                ctaText: 'View Offer'
              }, HEADERS);
              console.log('📋 Campaign updated with CTA link:', link.fullUrl);
            } catch (err) {
              console.error('Failed to update campaign with CTA link:', err);
            }
            fetchCampaigns(); // Refresh to show new link
          }}
          onSendWithLink={async (link) => {
            // Close the builder and send the campaign
            setCtaBuilderOpen(false);
            setCtaBuilderCampaign(null);
            // Update campaign with link first
            try {
              await axios.patch(`/api/campaigns/${ctaBuilderCampaign._id}`, {
                ctaUrl: link.fullUrl,
                ctaText: 'View Offer'
              }, HEADERS);
            } catch (err) {
              console.error('Failed to update campaign:', err);
            }
            // Then send (force resend since we just added a new CTA link)
            handleSendCampaign(ctaBuilderCampaign._id, true);
          }}
        />
      )}
    </Box>
  );
};

export default CampaignManagement;
